import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  TextField,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface VelocityItem {
  product_id: number;
  sku: string;
  title: string;
  vendor: string | null;
  supplier_name: string;
  current_stock: number;
  available_stock: number;
  units_sold: number;
  order_count: number;
  revenue: number;
  daily_velocity: number;
  weekly_velocity: number;
  monthly_velocity: number;
  days_of_stock: number | null;
  stock_status: string;
  last_sale_date: string | null;
  unit_cost: number;
}

interface Summary {
  period_days: number;
  products_sold: number;
  total_units_sold: number;
  total_revenue: number;
  avg_daily_units: number;
}

interface Warehouse {
  id: number;
  name: string;
}

export default function SalesVelocityReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();

  const [data, setData] = useState<VelocityItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // Filters
  const [days, setDays] = useState('30');
  const [minSales, setMinSales] = useState('0');
  const [sortBy, setSortBy] = useState('velocity');
  const [warehouseId, setWarehouseId] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  const loadWarehouses = useCallback(async () => {
    const result = await fetchApi<{ warehouses: Warehouse[] }>('/inventory/warehouses');
    if (result) {
      setWarehouses(result.warehouses || []);
    }
  }, [fetchApi]);

  const loadData = useCallback(async () => {
    let url = `/reports/sales-velocity?days=${days}&min_sales=${minSales}&sort_by=${sortBy}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;

    const result = await fetchApi<{
      data: VelocityItem[];
      summary: Summary;
      stock_status_counts: Record<string, number>;
    }>(url);

    if (result) {
      setData(result.data || []);
      setSummary(result.summary);
      setStatusCounts(result.stock_status_counts || {});
    }
  }, [fetchApi, days, minSales, sortBy, warehouseId]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; export_id: number; row_count: number }>(
        '/reports/sales-velocity/export',
        {
          method: 'POST',
          body: JSON.stringify({ days: parseInt(days), min_sales: parseInt(minSales) }),
        }
      );
      if (result?.success && result.export_id) {
        window.open(`/api/reports/exports/${result.export_id}/download`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = useMoney();

  const formatNumber = (value: number | null, decimals = 1) => {
    if (value === null) return '-';
    return value.toFixed(decimals);
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'critical' | 'warning' | 'success' | 'info' | 'attention'> = {
      'Critical': 'critical',
      'Low': 'warning',
      'Normal': 'success',
      'High': 'info',
      'Out of Stock': 'critical',
      'No Sales': 'attention',
    };
    return <Badge tone={tones[status] || 'info'}>{status}</Badge>;
  };

  const warehouseOptions = [
    { label: 'All Warehouses', value: '' },
    ...warehouses.map((w) => ({ label: w.name, value: w.id.toString() })),
  ];

  const sortOptions = [
    { label: 'Highest Velocity', value: 'velocity' },
    { label: 'Lowest Stock Days', value: 'stock_days' },
    { label: 'Highest Revenue', value: 'revenue' },
    { label: 'Most Units Sold', value: 'units' },
  ];

  const periodOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 14 days', value: '14' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 60 days', value: '60' },
    { label: 'Last 90 days', value: '90' },
  ];

  const tableRows = data.map((item) => [
    <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
      {item.sku}
    </Button>,
    <div title={item.title} style={{ width: 200, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
      {item.title}
    </div>,
    item.vendor || '-',
    item.supplier_name,
    item.current_stock.toString(),
    item.units_sold.toString(),
    formatNumber(item.daily_velocity),
    formatNumber(item.weekly_velocity),
    formatNumber(item.monthly_velocity),
    item.days_of_stock !== null ? `${Math.round(item.days_of_stock)}d` : '-',
    formatCurrency(item.revenue),
    getStatusBadge(item.stock_status),
  ]);

  return (
    <Page
      title="Sales Velocity Report"
      subtitle="Analyze product sales velocity and stock coverage"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: data.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Period</Text>
                  <Text variant="heading2xl" as="p">{summary.period_days} days</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Analysis period</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Products Sold</Text>
                  <Text variant="heading2xl" as="p">{summary.products_sold}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{summary.total_units_sold} total units</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Revenue</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(summary.total_revenue)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{formatCurrency(summary.total_revenue / summary.period_days)}/day avg</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Avg Daily Units</Text>
                  <Text variant="heading2xl" as="p">{formatNumber(summary.avg_daily_units)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Units sold per day</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Stock Status Summary */}
        {Object.keys(statusCounts).length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">Stock Status Distribution</Text>
                <InlineStack gap="400">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <InlineStack key={status} gap="200" blockAlign="center">
                      {getStatusBadge(status)}
                      <Text as="span" fontWeight="semibold">{count}</Text>
                    </InlineStack>
                  ))}
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start">
              <div style={{ width: 150 }}>
                <Select
                  label="Period"
                  options={periodOptions}
                  value={days}
                  onChange={setDays}
                />
              </div>
              <div style={{ width: 150 }}>
                <Select
                  label="Sort By"
                  options={sortOptions}
                  value={sortBy}
                  onChange={setSortBy}
                />
              </div>
              <div style={{ width: 180 }}>
                <Select
                  label="Warehouse"
                  options={warehouseOptions}
                  value={warehouseId}
                  onChange={setWarehouseId}
                />
              </div>
              <div style={{ width: 120 }}>
                <TextField
                  label="Min Sales"
                  type="number"
                  value={minSales}
                  onChange={setMinSales}
                  autoComplete="off"
                  min={0}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            ) : data.length === 0 ? (
              <EmptyState
                heading="No velocity data"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>No products match the current filters, or no sales data available.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={[
                  'text', 'text', 'text', 'text', 'numeric', 'numeric',
                  'numeric', 'numeric', 'numeric', 'text', 'numeric', 'text',
                ]}
                headings={[
                  'SKU', 'Product', 'Vendor', 'Supplier', 'Stock', 'Sold',
                  'Daily', 'Weekly', 'Monthly', 'Days', 'Revenue', 'Status',
                ]}
                rows={tableRows}
                footerContent={`${data.length} products`}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
